import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath } from 'react-router-dom';
import Intercom, { hide, shutdown, update } from '@intercom/messenger-js-sdk';
import type { InitType } from '@intercom/messenger-js-sdk/dist/types';

import { routes } from 'lane-shared/config';
import { ChannelsContext, UserDataContext } from 'lane-shared/contexts';
import history from '../../../helpers/history';
import { IntercomConfig, useIntercomConfig } from './useIntercomConfig';

type UserLike = {
  _id: string;
  _created?: Date | string;
  name?: string;
  profile?: { name?: string; email?: string } | null;
};
type ChannelLike = {
  _id: string;
  name?: string | null;
  profile?: { name?: string | null } | null;
};

function shouldCooldownIntercomCall(lastErrorAt: number | null): boolean {
  if (!lastErrorAt) return false;

  return Date.now() - lastErrorAt < 30_000;
}

function safelyShutdownIntercom(
  now: number,
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>
): void {
  try {
    shutdown();
  } catch {
    lastIntercomErrorAtRef.current = now;
  }
}

function safelyUpdateIntercom(
  now: number,
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>,
  settings?: Parameters<typeof update>[0]
): void {
  try {
    if (settings) update(settings);
  } catch {
    lastIntercomErrorAtRef.current = now;
  }
}

function safelyHideIntercom(
  now: number,
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>
): void {
  try {
    hide();
  } catch {
    lastIntercomErrorAtRef.current = now;
  }
}

function safelyBootOrUpdateIntercom(
  now: number,
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>,
  intercomPayload: InitType
): boolean {
  try {
    Intercom(intercomPayload);

    return true;
  } catch {
    lastIntercomErrorAtRef.current = now;

    return false;
  }
}

type SyncIntercomArgs = {
  isClient: boolean;
  isAdminView: boolean;
  shouldInitializeIntercom: boolean;
  channelIntegrationsLoaded: boolean;
  intercomConfig: IntercomConfig | null;
  intercomPayload: InitType | null;
  isBootedRef: React.MutableRefObject<boolean>;
  lastPayloadKeyRef: React.MutableRefObject<string | null>;
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>;
};

type ChannelSwitchArgs = {
  isClient: boolean;
  channelId: string | undefined;
  isBootedRef: React.MutableRefObject<boolean>;
  lastPayloadKeyRef: React.MutableRefObject<string | null>;
  lastIntercomErrorAtRef: React.MutableRefObject<number | null>;
  lastChannelIdRef: React.MutableRefObject<string | undefined>;
};

function resetIntercomOnChannelSwitch(args: ChannelSwitchArgs): void {
  if (!args.isClient) return;

  if (args.lastChannelIdRef.current && args.lastChannelIdRef.current !== args.channelId) {
    safelyShutdownIntercom(Date.now(), args.lastIntercomErrorAtRef);
    args.isBootedRef.current = false;
    args.lastPayloadKeyRef.current = null;
    args.lastIntercomErrorAtRef.current = null;
  }

  args.lastChannelIdRef.current = args.channelId;
}

function clearIntercomBootState(args: Pick<SyncIntercomArgs, 'isBootedRef' | 'lastPayloadKeyRef'>): void {
  args.isBootedRef.current = false;
  args.lastPayloadKeyRef.current = null;
}

function shutdownIntercomIfBooted(args: SyncIntercomArgs, now: number): void {
  if (!args.isBootedRef.current) return;

  safelyShutdownIntercom(now, args.lastIntercomErrorAtRef);
  clearIntercomBootState(args);
}

function syncIntercomWhenDisabled(args: SyncIntercomArgs, now: number): boolean {
  if (args.shouldInitializeIntercom) return false;

  if (args.isAdminView) return true;

  shutdownIntercomIfBooted(args, now);

  return true;
}

function syncIntercomWhenMissingConfig(args: SyncIntercomArgs, now: number): boolean {
  if (args.intercomConfig && args.intercomPayload) return false;

  if (!args.channelIntegrationsLoaded) return true;

  shutdownIntercomIfBooted(args, now);

  return true;
}

function syncIntercomBootOrUpdate(args: SyncIntercomArgs, now: number): void {
  const intercomPayload = args.intercomPayload;

  if (!intercomPayload) return;

  const payloadKey = JSON.stringify(intercomPayload);

  if (args.isBootedRef.current && args.lastPayloadKeyRef.current === payloadKey) {
    return;
  }

  const didSucceed = safelyBootOrUpdateIntercom(
    now,
    args.lastIntercomErrorAtRef,
    intercomPayload
  );

  if (!didSucceed) {
    clearIntercomBootState(args);

    return;
  }

  args.isBootedRef.current = true;
  args.lastPayloadKeyRef.current = payloadKey;
}

export function syncIntercomMessenger(args: SyncIntercomArgs): void {
  if (!args.isClient) return;

  const now = Date.now();

  if (syncIntercomWhenDisabled(args, now)) return;

  if (syncIntercomWhenMissingConfig(args, now)) return;

  if (shouldCooldownIntercomCall(args.lastIntercomErrorAtRef.current)) return;

  if (!args.intercomPayload) return;

  syncIntercomBootOrUpdate(args, now);
}

function getCreatedAt(user: UserLike): number | undefined {
  if (!user._created) return undefined;

  return Math.floor(new Date(user._created).getTime() / 1000);
}

function getCompany(primaryChannel: ChannelLike): { id: string; name: string } | undefined {
  const name =
    primaryChannel.name ?? primaryChannel.profile?.name ?? undefined;

  if (!primaryChannel._id || !name) return undefined;

  return { id: primaryChannel._id, name };
}

function buildIntercomPayload(
  intercomConfig: IntercomConfig,
  user: UserLike,
  primaryChannel: ChannelLike
): InitType {
  const payload: InitType = {
    app_id: intercomConfig.appId,
    intercom_user_jwt: intercomConfig.intercomUserJwt,
    user_id: user._id,
    name: user.profile?.name ?? user.name ?? undefined,
    email: user.profile?.email ?? undefined,
  };

  const createdAt = getCreatedAt(user);

  if (createdAt !== undefined) payload.created_at = createdAt;

  const company = getCompany(primaryChannel);

  if (company) payload.company = company;

  return payload;
}

function getIsAdminViewFromPathname(pathname: string): boolean {
  const channelMatch = matchPath(pathname, {
    path: routes.channelAdmin,
    exact: false,
    strict: false,
  });

  const portalManagementMatch = matchPath(pathname, {
    path: routes.portalManagement,
    exact: false,
    strict: false,
  });

  return Boolean(channelMatch || portalManagementMatch);
}

/**
 * Initializes the Intercom messenger in a common place so it is visible across
 * the entire member app. When the feature flag is on and the current channel has
 * an Intercom integration, calls Intercom() with app_id, user_id, name, email,
 * created_at, and company (channel). Calls shutdown() when the user logs out or
 * switches to a channel without Intercom.
 */
export function IntercomChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const isClient =
    typeof window !== 'undefined' && typeof document !== 'undefined';
  const { primaryChannel } = useContext(ChannelsContext);
  const { user, isLoggedIn } = useContext(UserDataContext);
  const [pathname, setPathname] = useState(() => {
    return isClient ? window.location.pathname : '';
  });

  useEffect(() => {
    if (!isClient) return;

    setPathname(window.location.pathname);

    const unlistenHistory =
      typeof history.listen === 'function'
        ? history.listen(({ pathname: nextPathname }) => {
            setPathname(nextPathname);
          })
        : () => {};

    const handlePopState = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      unlistenHistory();
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isClient]);

  const isAdminView = getIsAdminViewFromPathname(pathname);

  const shouldFetchIntercomConfig =
    Boolean(isLoggedIn) &&
    Boolean(primaryChannel?._id) &&
    Boolean(!isAdminView);

  const { intercomConfig, channelIntegrationsLoaded } = useIntercomConfig(
    primaryChannel?._id,
    shouldFetchIntercomConfig
  );

  const shouldInitializeIntercom =
    Boolean(isLoggedIn) &&
    Boolean(user?._id) &&
    Boolean(primaryChannel?._id) &&
    !isAdminView;

  const intercomPayload = useMemo(() => {
    if (
      !shouldInitializeIntercom ||
      !intercomConfig ||
      !user ||
      !primaryChannel
    ) {
      return null;
    }

    return buildIntercomPayload(intercomConfig, user, primaryChannel);
  }, [shouldInitializeIntercom, intercomConfig, user, primaryChannel]);

  const isBootedRef = useRef(false);
  const lastPayloadKeyRef = useRef<string | null>(null);
  const lastIntercomErrorAtRef = useRef<number | null>(null);
  const lastChannelIdRef = useRef<string | undefined>(primaryChannel?._id);

  useEffect(() => {
    resetIntercomOnChannelSwitch({
      isClient,
      channelId: primaryChannel?._id,
      isBootedRef,
      lastPayloadKeyRef,
      lastIntercomErrorAtRef,
      lastChannelIdRef,
    });
  }, [isClient, primaryChannel?._id]);

  useEffect(() => {
    syncIntercomMessenger({
      isClient,
      isAdminView,
      shouldInitializeIntercom,
      channelIntegrationsLoaded,
      intercomConfig,
      intercomPayload,
      isBootedRef,
      lastPayloadKeyRef,
      lastIntercomErrorAtRef,
    });
  }, [
    isClient,
    isAdminView,
    shouldInitializeIntercom,
    channelIntegrationsLoaded,
    intercomConfig,
    intercomPayload,
  ]);

  // Keep the default launcher visible whenever messenger is booted in member view.
  useEffect(() => {
    if (!isClient) return;

    if (!isBootedRef.current) return;

    if (shouldCooldownIntercomCall(lastIntercomErrorAtRef.current)) return;

    const now = Date.now();

    safelyUpdateIntercom(now, lastIntercomErrorAtRef, {
      hide_default_launcher: isAdminView,
    });

    if (isAdminView) {
      safelyHideIntercom(now, lastIntercomErrorAtRef);
    }
  }, [isClient, intercomPayload, isAdminView]);

  useEffect(() => {
    return () => {
      if (isBootedRef.current) {
        safelyShutdownIntercom(Date.now(), lastIntercomErrorAtRef);
        isBootedRef.current = false;
        lastPayloadKeyRef.current = null;
      }
    };
  }, []);

  return children;
}